import {
  Box,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@crayon/design-system-react";
import { PartnerMigrationState } from "api/client.generated";
import { AuthorizedTargetsContextProvider } from "components/hoc/PartnerMigration/AuthorizedTargetsContext";
import BillingMappingsStep from "components/hoc/PartnerMigration/Steps/BIllingMappingsStep";
import MigrationCompletedStep from "components/hoc/PartnerMigration/Steps/MigrationCompletedStep";
import PriceRulesStep from "components/hoc/PartnerMigration/Steps/PriceRulesStep";
import ProductMappingsStep from "components/hoc/PartnerMigration/Steps/ProductMappingsStep";
import ProgramsStep from "components/hoc/PartnerMigration/Steps/ProgramsStep";
import PSAsUsageStep from "components/hoc/PartnerMigration/Steps/PSAsUsageStep";
import SourceConnectionStep from "components/hoc/PartnerMigration/Steps/SourceConnectionStep";
import TargetConnectionsStep from "components/hoc/PartnerMigration/Steps/TargetConnectionsStep";
import WelcomeStep from "components/hoc/PartnerMigration/Steps/WelcomeStep";
import LoadingScreen from "components/primitives/LoadingScreen";
import TextHeader from "components/primitives/TextHeader";
import { useNotificationContext } from "context/notificationContext";
import { useSelectedPartnerContext } from "context/selectedPartnerContext";
import usePartnerMigration from "hooks/api/PartnerMigration/usePartnerMigration";
import useApi from "hooks/api/useApi";
import { InfoOutlinedIcon } from "images/MuiIcons";
import { useCallback, useEffect, useMemo, useState } from "react";
import NotificationMessage from "types/notification-message";

interface MigrationStepItem {
  state: PartnerMigrationState;
  label: string;
  info?: string;
  content: React.ReactNode;
}

const MigratePartnerPage = () => {
  const { migrationData, isMigrationDataLoading } = usePartnerMigration();
  const { partner } = useSelectedPartnerContext();
  const { partnersMigrationClient } = useApi();
  const { raiseErrorNotification } = useNotificationContext();

  // if 0, it triggers rendering 1st step component
  const [activeStep, setActiveStep] = useState(-1);

  const onStepComplete = useCallback(
    async (state: PartnerMigrationState) => {
      try {
        await partnersMigrationClient.updatePartnerMigrationState({ state });
        setActiveStep((prev) => prev + 1);
      } catch (e) {
        raiseErrorNotification(NotificationMessage.FAILED_TO_UPDATE_MIGRATION_STATE);
      }
    },
    [partnersMigrationClient, raiseErrorNotification],
  );

  const migrationSteps: MigrationStepItem[] = useMemo<MigrationStepItem[]>(
    () => [
      {
        state: PartnerMigrationState.NotStarted,
        label: "Start",
        content: (
          <WelcomeStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.SourceConnection)}
          />
        ),
      },
      {
        state: PartnerMigrationState.SourceConnection,
        label: "Set-up Cloud-iQ Connection",
        content: (
          <SourceConnectionStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.TargetConnections)}
          />
        ),
      },
      {
        state: PartnerMigrationState.TargetConnections,
        label: "Choose PSA configurations to migrate",
        info: "This will copy configured connections",
        content: (
          <TargetConnectionsStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.Programs)}
          />
        ),
      },
      {
        state: PartnerMigrationState.Programs,
        label: "Choose program configurations to migrate",
        info: "This will copy configured programs",
        content: (
          <ProgramsStep onStepComplete={() => onStepComplete(PartnerMigrationState.PriceRules)} />
        ),
      },
      {
        state: PartnerMigrationState.PriceRules,
        label: "Choose price rules to migrate",
        info: "This will copy active or future price rules",
        content: (
          <PriceRulesStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.ProductMappings)}
          />
        ),
      },
      {
        state: PartnerMigrationState.ProductMappings,
        label: "Choose product mappings to migrate",
        info: "This will copy existing product mappings, so PSA products will be reused",
        content: (
          <ProductMappingsStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.BillingMappings)}
          />
        ),
      },
      {
        state: PartnerMigrationState.BillingMappings,
        label: "Choose billing mappings to migrate",
        info: "This will copy existing billing mappings",
        content: (
          <BillingMappingsStep
            onStepComplete={() => onStepComplete(PartnerMigrationState.PSAsUsageState)}
          />
        ),
      },
      {
        state: PartnerMigrationState.PSAsUsageState,
        label: "Update PSA usage state",
        info: "This will updated subscription ID in usage records",
        content: (
          <PSAsUsageStep onStepComplete={() => onStepComplete(PartnerMigrationState.Completed)} />
        ),
      },
      {
        state: PartnerMigrationState.Completed,
        label: "Completed",
        content: <MigrationCompletedStep />,
      },
    ],
    [onStepComplete],
  );

  useEffect(() => {
    if (isMigrationDataLoading || !migrationData) return;
    const stepIndex = migrationSteps.findIndex((x) => x.state === migrationData.state);
    if (stepIndex > -1) setActiveStep(stepIndex);
  }, [isMigrationDataLoading, migrationData, setActiveStep, migrationSteps]);

  if (isMigrationDataLoading) return <LoadingScreen />;

  if (!migrationData)
    return (
      <Typography variant="body1">Sorry, migration is not configured for this partner</Typography>
    );

  if (migrationData.sourcePartner.id.toLowerCase() === partner?.id.toLowerCase())
    return (
      <Typography variant="body1">
        Please, switch to &apos;{migrationData.targetPartner.name}&apos; to proceed with migration
      </Typography>
    );

  return (
    <Box>
      <TextHeader testId="page-header" label="Partner migration" />
      <AuthorizedTargetsContextProvider>
        <Stepper orientation="vertical" activeStep={activeStep}>
          {migrationSteps.map((x, index) => (
            <Step key={x + String(index)}>
              <StepLabel testId={`step-${index + 1}-label`}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" testId={`step-${index + 1}-typography`}>
                    {x.label}
                  </Typography>
                  {activeStep === index && x.info && (
                    <Tooltip title={x.info}>
                      <InfoOutlinedIcon color="primary" />
                    </Tooltip>
                  )}
                </Box>
              </StepLabel>
              <StepContent>{x.content}</StepContent>
            </Step>
          ))}
        </Stepper>
      </AuthorizedTargetsContextProvider>
    </Box>
  );
};

export default MigratePartnerPage;
